import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Target, MapPin, Monitor, Users, Mail, BarChart3 } from 'lucide-react';
interface UseCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const stages = [{
  icon: Target,
  phase: 'Pre-Congress',
  weeks: '4–6 weeks before',
  channel: 'Email, WhatsApp, Social, QR on invites',
  snak: 'Peer Poll Snak™',
  description: 'Distribute a teaser Snak™ asking "When do you escalate beyond RAAS blockade?" via email and WhatsApp. HCPs answer, see peer benchmarks, and receive a CTA to visit the CSL booth for the full data.',
  color: '#E60012'
}, {
  icon: MapPin,
  phase: 'Vicinity',
  weeks: 'Day of congress',
  channel: 'Geo-targeted ads, transit QR codes',
  snak: 'Quiz Challenge Snak™',
  description: 'Geo-targeted ads in ride-share apps near the venue serve a quick IgA Nephropathy quiz. Correct answers unlock a booth visit incentive with directions to CSL Booth #215.',
  color: '#C5000F'
}, {
  icon: Monitor,
  phase: 'Aisle',
  weeks: 'During congress',
  channel: 'iPads, touchscreens, QR on booth exterior',
  snak: 'Self-Guided Explorer Snak™',
  description: 'Stationary iPads outside the booth perimeter let HCPs browse disease areas (IgA Nephropathy, HAE, hemophilia) at their own pace. Menu Cue™ lets them choose their topic. No rep interaction required — lowering the barrier to engage.',
  color: '#E60012'
}, {
  icon: Users,
  phase: 'Booth',
  weeks: 'During congress',
  channel: 'Rep-led iPads',
  snak: 'Deep-Dive Conversation Snak™',
  description: 'Once inside the booth, reps use guided Snaks™ to walk HCPs through clinical data, capture specialty and interest via Input Cues, and collect consent for follow-up — all within a single seamless flow.',
  color: '#C5000F'
}, {
  icon: Mail,
  phase: 'Post-Congress',
  weeks: '1–2 weeks after',
  channel: 'Email, WhatsApp',
  snak: 'Follow-Up Recap Snak™',
  description: 'Opted-in HCPs receive a personalized "Thank You" Snak™ with session highlights, downloadable clinical summaries, and a Rating Cue™ to capture feedback. Data feeds into CRM for rep follow-up.',
  color: '#E60012'
}];
export function UseCaseModal({
  isOpen,
  onClose
}: UseCaseModalProps) {
  return <AnimatePresence data-id="element-709">
      {isOpen && <motion.div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} data-id="element-710">
          <motion.div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0.9,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} onClick={e => e.stopPropagation()} data-id="element-711">
            {/* Header */}
            <div className="bg-[#1A1A2E] px-8 py-6 text-white shrink-0" data-id="element-712">
              <div className="flex items-center justify-between" data-id="element-713">
                <div data-id="element-714">
                  <div className="flex items-center gap-2 mb-2" data-id="element-715">
                    <div className="w-6 h-[2px] bg-[#E60012]" data-id="element-716" />
                    <span className="text-[#E60012] text-xs font-medium tracking-wide uppercase" data-id="element-717">
                      Use Case
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold" data-id="element-718">
                    CSL × Snakker™ at ERA Congress
                  </h2>
                  <p className="text-white/60 text-sm mt-1" data-id="element-719">
                    Full-funnel activation for IgA Nephropathy awareness
                  </p>
                </div>
                <button onClick={onClose} className="text-white/50 hover:text-white transition-colors p-2 rounded-full hover:bg-white/10" data-id="element-720">
                  <X className="w-6 h-6" data-id="element-721" />
                </button>
              </div>
            </div>

            {/* Context bar */}
            <div className="bg-[#FDF8F8] px-8 py-4 border-b border-gray-100 shrink-0" data-id="element-722">
              <div className="flex flex-wrap gap-6 text-sm" data-id="element-723">
                <div data-id="element-724">
                  <span className="text-gray-400 text-xs uppercase tracking-wide" data-id="element-725">
                    Client
                  </span>
                  <p className="font-bold text-[#2D2926]" data-id="element-726">CSL Vifor</p>
                </div>
                <div data-id="element-727">
                  <span className="text-gray-400 text-xs uppercase tracking-wide" data-id="element-728">
                    Congress
                  </span>
                  <p className="font-bold text-[#2D2926]" data-id="element-729">
                    ERA 2026 (European Renal Association)
                  </p>
                </div>
                <div data-id="element-730">
                  <span className="text-gray-400 text-xs uppercase tracking-wide" data-id="element-731">
                    Disease Area
                  </span>
                  <p className="font-bold text-[#2D2926]" data-id="element-732">IgA Nephropathy</p>
                </div>
                <div data-id="element-733">
                  <span className="text-gray-400 text-xs uppercase tracking-wide" data-id="element-734">
                    Audience
                  </span>
                  <p className="font-bold text-[#2D2926]" data-id="element-735">
                    Nephrologists, GPs, Nurses
                  </p>
                </div>
              </div>
            </div>

            {/* Objective */}
            <div className="px-8 py-5 border-b border-gray-100 shrink-0" data-id="element-736">
              <h3 className="text-sm font-bold text-[#2D2926] mb-2 flex items-center gap-2" data-id="element-737">
                <BarChart3 className="w-4 h-4 text-[#E60012]" data-id="element-738" />
                Objective
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-739">
                Activate the full congress journey — from setting the clinical
                agenda weeks before ERA, through vicinity nudging and aisle
                engagement, to deep booth conversations and post-congress
                follow-up. Each touchpoint uses a tailored Snak™ to lower
                barriers and move HCPs closer to meaningful engagement with
                CSL's IgA Nephropathy data.
              </p>
            </div>

            {/* Journey stages */}
            <div className="flex-1 overflow-auto px-8 py-6" data-id="element-740">
              <h3 className="text-sm font-bold text-[#2D2926] mb-5" data-id="element-741">
                The Snak™ Journey
              </h3>
              <div className="space-y-4" data-id="element-742">
                {stages.map((stage, i) => {
              const Icon = stage.icon;
              return <div key={i} className="flex gap-4" data-id="element-743">
                      {/* Timeline dot + line */}
                      <div className="flex flex-col items-center shrink-0" data-id="element-744">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0" style={{
                    backgroundColor: stage.color
                  }} data-id="element-745">
                          <Icon className="w-5 h-5" data-id="element-746" />
                        </div>
                        {i < stages.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-1" data-id="element-747" />}
                      </div>

                      {/* Content */}
                      <div className="pb-4 flex-1 min-w-0" data-id="element-748">
                        <div className="flex items-baseline gap-3 mb-1" data-id="element-749">
                          <h4 className="font-bold text-[#2D2926]" data-id="element-750">
                            {stage.phase}
                          </h4>
                          <span className="text-xs text-gray-400" data-id="element-751">
                            {stage.weeks}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-2" data-id="element-752">
                          <span className="text-[10px] font-medium bg-[#E60012]/10 text-[#E60012] px-2 py-0.5 rounded-full" data-id="element-753">
                            {stage.snak}
                          </span>
                          <span className="text-[10px] font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full" data-id="element-754">
                            {stage.channel}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-755">
                          {stage.description}
                        </p>
                      </div>
                    </div>;
            })}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-[#FDF8F8] px-8 py-4 border-t border-gray-100 shrink-0" data-id="element-756">
              <p className="text-xs text-gray-400 text-center" data-id="element-757">
                This use case describes a proposed Snakker™ activation for CSL
                at ERA Congress. All Snak™ content is subject to medical-legal
                review.
              </p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}